import React from 'react';
import PropTypes from 'prop-types';

import FileInput from 'app/components/common/file-input';
import RteEditor from 'app/components/common/rich-text/editor';

// the form for both new & existing comments: a "Comment" sect + an "Attachments" sect
// each sect shows a button until opened, then its input
// with startCollapsed, starts as just the 2 buttons (cancel collapses it back)
// with allowFiles off, it's just the editor (no headings)
class CommentForm extends React.PureComponent {

  constructor(props) {
    super(props);

    const {initialBody, initialFiles, startCollapsed} = props;
    this.state = {
      body: initialBody,
      files: initialFiles,
      showEditor: !startCollapsed && (!!initialBody || !initialFiles.length),
      showFiles: !startCollapsed && !!initialFiles.length,
      focusEditor: props.focusOnMount,
      pending: false,
    };

    this.refFiles = React.createRef();

    this.onChangeBody = this.onChangeBody.bind(this);
    this.onChangeFiles = this.onChangeFiles.bind(this);
    this.onClickShowEditor = this.onClickShowEditor.bind(this);
    this.onClickShowFiles = this.onClickShowFiles.bind(this);
    this.onClickCancel = this.onClickCancel.bind(this);
    this.onClickSubmit = this.onClickSubmit.bind(this);
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  get attrs() {
    return {
      body: (this.state.body || '').trim(),
      files: this.props.allowFiles ? this.state.files : [],
    };
  }

  get submitDisabled() {
    const {body, files} = this.attrs;
    return this.state.pending || (!body && !files.length);
  }

  get isCollapsed() {
    return this.props.allowFiles && !this.state.showEditor && !this.state.showFiles;
  }

  get showCancel() {
    return !!(this.props.onCancel || this.props.startCollapsed);
  }

  // jump straight to the file picker rather than making them click FileInput's own button too
  openPicker() {
    const input = this.refFiles.current?.querySelector('.file-input-actual');
    input && input.click();
  }

  onChangeBody(body) {
    this.setState({body});
  }

  onChangeFiles(files) {
    this.setState({files});
  }

  onClickShowEditor() {
    this.setState({showEditor: true, focusEditor: true});
  }

  onClickShowFiles() {
    this.setState({showFiles: true}, () => this.openPicker());
  }

  onClickCancel() {
    if (this.props.onCancel) return this.props.onCancel();
    this.setState({body: '', files: [], showEditor: false, showFiles: false});
  }

  async onClickSubmit() {
    if (this.submitDisabled) return;
    this.setState({pending: true});
    try {
      await this.props.onSubmit(this.attrs);
    } catch (error) {
      console.error(error);
    }
    // parents typically unmount or remount this on success
    if (!this.unmounted) this.setState({pending: false});
  }

  renderEditor() {
    return (
      <RteEditor
        onChange={this.onChangeBody}
        initialMd={this.state.body}
        placeholder={this.props.placeholder}
        className="gacom-form-editor"
        focusOnMount={this.state.focusEditor}
        hideTextBlocks
      />
    );
  }

  renderBtnShowEditor() {
    return <button className="btn small secondary blue" onClick={this.onClickShowEditor}>Add a Comment</button>;
  }

  renderBtnShowFiles() {
    return <button className="btn small secondary blue" onClick={this.onClickShowFiles}>Attach a File</button>;
  }

  renderActions() {
    const {submitLabel, submitPendingLabel} = this.props;
    return (
      <div className="gacom-form-actions">
        {this.showCancel && (
          <button className="btn small secondary ghost slate" onClick={this.onClickCancel}>Cancel</button>
        )}
        <button className="btn small blue" disabled={this.submitDisabled} onClick={this.onClickSubmit}>{this.state.pending ? submitPendingLabel : submitLabel}</button>
      </div>
    );
  }

  render() {
    const {allowFiles, className, startCollapsed} = this.props;
    const {showEditor, showFiles, files} = this.state;
    const boxClass = startCollapsed ? '_p-18 _br-2 _bg-ice' : '';

    if (!allowFiles) return (
      <div className={`gacom-form ${className}`}>
        {this.renderEditor()}
        {this.renderActions()}
      </div>
    );

    if (this.isCollapsed) return (
      <div className={`gacom-form ${className}`}>
        <div className="gacom-form-start">
          {this.renderBtnShowEditor()}
          {this.renderBtnShowFiles()}
        </div>
      </div>
    );

    return (
      <div className={`gacom-form ${boxClass} ${className}`}>
        <div className="gacom-form-sect">
          <h4 className="gacom-form-sect-heading">Comment</h4>
          {showEditor ? this.renderEditor() : this.renderBtnShowEditor()}
        </div>
        <div className="gacom-form-sect">
          <h4 className="gacom-form-sect-heading">Attachments</h4>
          {showFiles ? (
            <div ref={this.refFiles}>
              <FileInput files={files} onChange={this.onChangeFiles} maxFiles={10} onGray />
            </div>
          ) : this.renderBtnShowFiles()}
        </div>
        {this.renderActions()}
      </div>
    );
  }

}

CommentForm.propTypes = {
  onSubmit: PropTypes.func.isRequired, // receives {body, files}; may return a promise
  onCancel: PropTypes.func,
  initialBody: PropTypes.string,
  initialFiles: PropTypes.arrayOf(PropTypes.string),
  allowFiles: PropTypes.bool,
  startCollapsed: PropTypes.bool,
  focusOnMount: PropTypes.bool,
  placeholder: PropTypes.string,
  submitLabel: PropTypes.string,
  submitPendingLabel: PropTypes.string,
  className: PropTypes.string,
};

CommentForm.defaultProps = {
  onCancel: null,
  initialBody: '',
  initialFiles: [],
  allowFiles: true,
  startCollapsed: false,
  focusOnMount: false,
  placeholder: 'Write your remarks...',
  submitLabel: 'Post',
  submitPendingLabel: 'Posting...',
  className: '',
};

export default CommentForm;
